// Client for the auth plane (auth.formpipeline.io). The session lives in an
// HttpOnly cookie on the auth host, so every call sends credentials and nothing
// here touches storage. Access tokens are kept in memory only.

export const AUTH_BASE = (process.env.REACT_APP_AUTH_BASE || 'https://auth.formpipeline.io').replace(/\/+$/, '');

export interface Person {
  id: string;
  name: string;
  email: string;
}

export interface Membership {
  accountId: string;
  slug: string;
  name: string;
  status: string;
}

export interface SignedIn {
  status: 'ok';
  person: Person;
  memberships: Membership[];
  recoveryCodes?: string[];
}

export type LoginResult =
  | SignedIn
  | { status: 'mfa_required'; challenge: string }
  | { status: 'mfa_enrollment_required'; challenge: string; otpauthUri: string };

export interface IssuedToken {
  accessToken: string;
  expiresIn: number;
  account: { id: string; slug: string; name: string };
  apiBaseUrl: string;
}

export class AuthError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(AUTH_BASE + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new AuthError('Could not reach the sign-in service. Check your connection.', 0);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const json = await response.json().catch(() => null);
  if (!response.ok || !json?.success) {
    throw new AuthError(json?.error || `Sign-in failed (${response.status}).`, response.status);
  }
  return json.data as T;
}

export const authClient = {
  setPassword: (token: string, password: string) => call<void>('/auth/password', { token, password }),
  login: (email: string, password: string) => call<LoginResult>('/auth/login', { email, password }),
  verifyMfa: (challenge: string, code: { code?: string; recoveryCode?: string }) =>
    call<SignedIn>('/auth/mfa', { challenge, ...code }),
  token: (accountId: string) => call<IssuedToken>('/auth/token', { accountId }),
  logout: () => call<void>('/auth/logout'),
  /** Who is signed in and which accounts they can open, without rotating the session. */
  session: () => call<SignedIn>('/auth/session'),
};

/** The base32 secret inside an otpauth:// URI, for typing into an authenticator by hand. */
export function otpSecret(uri: string): string | null {
  const match = /[?&]secret=([A-Z2-7=]+)/i.exec(uri);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Decodes a JWT's header (part 0) or claims (part 1) for display. It does not verify
 * the signature; data planes do that.
 */
export function decodeJwt(jwt: string, part: 0 | 1 = 1): Record<string, unknown> | null {
  try {
    const segment = jwt.split('.')[part].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(segment.padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=')));
  } catch {
    return null;
  }
}
